import type {
  ContactChannel,
  PickedAddress,
  ProblemNewDraft,
} from '@/public/features/problem/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/shared/lib/api'
import type {
  CreateProblemCardBody,
  CreatedProblemCard,
  ProblemCardEditForm,
  UpdatedProblemCard,
} from '@/shared/lib/apiTypes'
import { ApiError } from '@/shared/lib/http'
import { getSessionId } from '@/shared/lib/session'
import { toggle } from '@/shared/lib/toggle'
import {
  ANALYSIS_WAIT_MS,
  ATTEMPT_MAX,
  CATEGORY_MAX,
  CONSENTS,
  GATE_INSUFFICIENT,
  RAW_TEXT_MAX,
  RAW_TEXT_MIN,
  REVISION_STEP,
  TARGET_MAX,
  TOTAL_STEPS,
  type ConsentKey,
} from '@/public/features/problem/constants/problemNew'

const PHONE_DIGITS = /^01[016789]\d{7,8}$/

function digitsOf(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatPhone(value: string): string {
  const d = digitsOf(value).slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`
  const mid = d.length === 11 ? 7 : 6
  return `${d.slice(0, 3)}-${d.slice(3, mid)}-${d.slice(mid)}`
}

export function isPhoneValid(value: string): boolean {
  const d = digitsOf(value)
  return d.length === 0 || PHONE_DIGITS.test(d)
}

export function isEmailValid(value: string): boolean {
  return value.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

const EMPTY_ADDRESS: PickedAddress = {
  text: '',
  regionCode: '',
  matchedName: '',
  legalCode: '',
  legalName: '',
  postalCode: '',
  lat: null,
  lng: null,
}

/** 수정 폼이 준 주소를 화면이 쓰는 모양으로 옮긴다. 좌표는 보관 대상이 아니라 비워 둔다. */
function addressOf(form: ProblemCardEditForm): PickedAddress {
  const a = form.address
  if (a === undefined) return EMPTY_ADDRESS
  return {
    text: a.text,
    regionCode: a.adminCode,
    matchedName: a.adminName ?? '',
    legalCode: a.legalCode ?? '',
    legalName: a.legalName ?? '',
    postalCode: a.postalCode ?? '',
    lat: null,
    lng: null,
  }
}

export interface ProblemEditTarget {
  id: string
  token: string
}

export function problemFlowBasePath(edit?: ProblemEditTarget): string {
  return edit === undefined ? '/problems/new' : `/problems/${edit.id}/edit`
}

export function useProblemNewDraft(step: number, edit?: ProblemEditTarget): ProblemNewDraft {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const basePath = problemFlowBasePath(edit)
  const stepPath = useCallback(
    (n: number): string => {
      const tail = n <= 1 ? '' : `/${n}`
      if (edit === undefined) return `${basePath}${tail}`
      return `${basePath}${tail}?t=${encodeURIComponent(edit.token)}`
    },
    [basePath, edit],
  )

  const [categoryCodes, setCategoryCodes] = useState<string[]>([])
  const [targetCodes, setTargetCodes] = useState<string[]>([])
  const [address, setAddress] = useState<PickedAddress>(EMPTY_ADDRESS)
  const [rawText, setRawTextValue] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactChannel, setContactChannel] = useState<ContactChannel>('phone')
  const [contactPhone, setContactPhone] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [consents, setConsents] = useState<ConsentKey[]>([])

  const [showRevisionNotice, setShowRevisionNotice] = useState(false)

  const [attempt, setAttempt] = useState(1)

  const editForm = useQuery({
    queryKey: ['problem-card-edit', edit?.id, edit?.token],
    queryFn: () => api.problemCardEditForm(edit?.id ?? '', edit?.token ?? ''),
    enabled: edit !== undefined,
    retry: false,
    staleTime: Infinity,
  })

  const loaded = editForm.data
  const [filled, setFilled] = useState(false)
  useEffect(() => {
    if (loaded === undefined || filled) return
    setFilled(true)
    setRawTextValue(loaded.rawText)
    setCategoryCodes(loaded.categoryCodes ?? [])
    setTargetCodes(loaded.targetCodes ?? [])
    setAddress(addressOf(loaded))
    setContactName(loaded.contactName ?? '')
    setContactPhone(loaded.contactPhone ?? '')
    setContactEmail(loaded.contactEmail ?? '')
    if ((loaded.contactPhone ?? '') === '' && (loaded.contactEmail ?? '') !== '') {
      setContactChannel('email')
    }
    setConsents(CONSENTS.map((c) => c.key).filter((k) => (loaded.consents ?? []).includes(k)))
  }, [loaded, filled])

  const mutation = useMutation<CreatedProblemCard | UpdatedProblemCard, unknown, CreateProblemCardBody>({
    mutationFn: (body) =>
      edit === undefined
        ? api.createProblemCard(body)
        : api.updateProblemCard(edit.id, edit.token, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['problem-cards'] })
    },
  })

  const [submitCount, setSubmitCount] = useState(0)
  const [waitDone, setWaitDone] = useState(false)

  useEffect(() => {
    if (submitCount === 0) return
    setWaitDone(false)
    const timer = window.setTimeout(() => setWaitDone(true), ANALYSIS_WAIT_MS)
    return () => window.clearTimeout(timer)
  }, [submitCount])

  const dismissRevisionNotice = useCallback(() => setShowRevisionNotice(false), [])

  const goRevise = useCallback(() => {
    setShowRevisionNotice(true)
    setAttempt((n) => Math.min(n + 1, ATTEMPT_MAX))
    mutation.reset()
    navigate(stepPath(REVISION_STEP))
  }, [mutation, navigate, stepPath])

  const toggleCategory = useCallback((code: string) => {
    setCategoryCodes((prev) => toggle(prev, code, CATEGORY_MAX))
  }, [])

  const toggleTarget = useCallback((code: string) => {
    setTargetCodes((prev) => toggle(prev, code, TARGET_MAX))
  }, [])

  const setRawText = useCallback((v: string) => {
    setRawTextValue(v.slice(0, RAW_TEXT_MAX))
  }, [])

  const setPhone = useCallback((v: string) => {
    setContactPhone(formatPhone(v))
  }, [])

  const switchContactChannel = useCallback(() => {
    setContactChannel((prev) => (prev === 'phone' ? 'email' : 'phone'))
  }, [])

  const toggleConsent = useCallback((key: ConsentKey) => {
    setConsents((prev) => toggle(prev, key))
  }, [])

  const contactValue = contactChannel === 'phone' ? contactPhone : contactEmail

  const isStepDone = (n: number): boolean => {
    switch (n) {
      case 1:
        return categoryCodes.length > 0
      case 2:
        return targetCodes.length > 0 && address.regionCode !== ''
      case 3:
        return rawText.trim().length >= RAW_TEXT_MIN
      default:
        return (
          contactName.trim().length > 0 &&
          contactValue.trim().length > 0 &&
          CONSENTS.every((c) => consents.includes(c.key))
        )
    }
  }

  const hasContactError =
    contactChannel === 'phone' ? !isPhoneValid(contactPhone) : !isEmailValid(contactEmail)

  const firstIncompleteStep = (() => {
    for (let n = 1; n <= TOTAL_STEPS; n += 1) {
      if (!isStepDone(n)) return n
    }
    return TOTAL_STEPS
  })()

  const canGoNext =
    step === TOTAL_STEPS
      ? firstIncompleteStep === TOTAL_STEPS && isStepDone(TOTAL_STEPS) && !hasContactError
      : isStepDone(step)

  const goPrev = useCallback(() => {
    if (step <= 1) {
      navigate(-1)
      return
    }
    navigate(stepPath(step - 1))
  }, [navigate, step, stepPath])

  const goNext = useCallback(() => {
    if (step < TOTAL_STEPS) navigate(stepPath(step + 1))
  }, [navigate, step, stepPath])

  const submit = useCallback(() => {
    if (!canGoNext || mutation.isPending) return

    const sessionId = getSessionId()
    const contactKey = contactChannel === 'phone' ? 'contactPhone' : 'contactEmail'
    const contactTrimmed = contactValue.trim()
    const contact = {
      contactName: contactName.trim(),
      consents,
      ...(contactTrimmed.length > 0 ? { [contactKey]: contactTrimmed } : {}),
    }

    const body: CreateProblemCardBody = {
      rawText: rawText.trim(),
      targetCodes,
      address: {
        text: address.text.trim(),
        adminCode: address.regionCode,
        adminName: address.matchedName,
        ...(address.legalCode !== '' ? { legalCode: address.legalCode } : {}),
        ...(address.legalName !== '' ? { legalName: address.legalName } : {}),
        ...(address.postalCode !== '' ? { postalCode: address.postalCode } : {}),
      },
      attempt,
      ...(sessionId !== undefined && edit === undefined ? { sessionId } : {}),
      ...(categoryCodes.length > 0 ? { categoryCodes } : {}),
      ...contact,
    }
    setSubmitCount((n) => n + 1)
    mutation.mutate(body)
  }, [
    attempt,
    canGoNext,
    mutation,
    rawText,
    targetCodes,
    address.regionCode,
    address.text,
    categoryCodes,
    contactChannel,
    contactName,
    contactPhone,
    contactEmail,
    consents,
  ])

  return {
    step,
    stage:
      submitCount === 0
        ? 'form'
        : mutation.isPending || !waitDone
          ? 'analyzing'
          : mutation.isSuccess
            ? 'done'
            : isGateRejection(mutation.error)
              ? 'revision'
              : 'form',
    revisionReasons: isGateRejection(mutation.error) ? mutation.error.detail : [],
    isDone: mutation.isSuccess,
    isSubmitting: mutation.isPending,
    error: mutation.error,
    showRevisionNotice,
    dismissRevisionNotice,
    goRevise,

    categoryCodes,
    targetCodes,
    address,
    rawText,
    contactName,
    contactChannel,
    contactPhone,
    contactEmail,
    consents,

    toggleCategory,
    toggleTarget,
    setAddress,
    setRawText,
    setContactName,
    setContactPhone: setPhone,
    setContactEmail,
    switchContactChannel,
    toggleConsent,

    canGoNext,
    hasContactError,
    isEdit: edit !== undefined,
    isLoadingForm: editForm.isPending && edit !== undefined,
    formError: editForm.error,
    editLinkPath:
      mutation.data !== undefined && 'editPath' in mutation.data ? mutation.data.editPath : null,
    firstIncompleteStep,
    goPrev,
    goNext,
    submit,
  }
}

function isGateRejection(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 422 && error.code === GATE_INSUFFICIENT
}
